/**
 * Ticket WK5: Pflege der öffentlichen Wahlkalender-Seiten je Bundesland.
 *
 * Ticket WK8: Der eigene Abschnitt am Seitenende entfällt. Die Freischaltung
 * steht als Schalter in der Zeile der Amtszeiten, die Textfelder öffnet ein
 * Knopf in derselben Zeile. Der Schreibweg ist derselbe, den auch die
 * Leseschicht nutzt – keine zweite Schreibstelle.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listWahlkalenderLandesseiten,
  saveWahlkalenderLandesseite,
} from "@/lib/wahlkalender-landesseite.functions";
import { bundeslandSlug, landesseitePath } from "@/lib/wahlkalender-landesseite";
import { BUNDESLAND_LABELS } from "@/lib/wahlkalender";
import type { LandesseiteAdminRow } from "@/lib/wahlkalender-landesseite.server";
import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Bundesland } from "@/lib/wahlkalender";
import { supabase } from "@/integrations/supabase/client";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";
import { LEAD_MAGNET_PREVIEW_BUCKET } from "@/lib/lead-magnets";
import { permanentImageUrl, pathFromPermanentImageUrl } from "@/lib/permanent-image";
import { sanitizeStorageFilename } from "@/lib/storage-filename";

export const WAHLKALENDER_IMAGE_PREFIX = "wahlkalender/";
const ALLOWED_SHARE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export function wahlkalenderImagePath(bundesland: Bundesland, fileName: string, id: string): string {
  return `${WAHLKALENDER_IMAGE_PREFIX}${bundesland}/${id}/${sanitizeStorageFilename(fileName)}`;
}

export function ownWahlkalenderImagePath(value: string | null): string | null {
  if (!value) return null;
  const path = pathFromPermanentImageUrl("wahlkalender", value);
  return path?.startsWith(WAHLKALENDER_IMAGE_PREFIX) ? path : null;
}

/** Gemeinsamer Lese- und Schreibweg der Landesseiten. */
function useLandesseiten() {
  const queryClient = useQueryClient();
  const load = useServerFn(listWahlkalenderLandesseiten);
  const save = useServerFn(saveWahlkalenderLandesseite);

  const query = useQuery({
    queryKey: ["admin", "wahlkalender-landesseiten"],
    queryFn: () => load(),
  });

  const mutation = useMutation({
    mutationFn: (row: LandesseiteAdminRow) => save({ data: row }),
    onSuccess: () => {
      toast.success("Landesseite gespeichert.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "wahlkalender-landesseiten"] });
    },
    onError: (error: unknown) =>
      toast.error((error as Error)?.message ?? "Speichern fehlgeschlagen."),
  });

  return { query, mutation };
}

/**
 * Ticket WK8: Freischaltung und Bearbeiten-Knopf in der Zeile der Amtszeiten.
 * Ein nicht freigeschaltetes Bundesland ist für Besucher nicht erreichbar.
 */
export function LandesseitePublishCell({ bundesland }: { bundesland: Bundesland }) {
  const { query, mutation } = useLandesseiten();
  const [open, setOpen] = useState(false);
  const row = (query.data ?? []).find((entry) => entry.bundesland === bundesland) ?? null;

  if (query.isLoading) {
    return <span className="font-body text-xs text-muted-foreground">wird geladen …</span>;
  }
  if (!row) {
    return (
      <span className="font-body text-xs text-muted-foreground">
        Keine Landesseite angelegt.
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 font-body text-xs">
        <Switch
          checked={row.published}
          disabled={mutation.isPending}
          onCheckedChange={(checked) => mutation.mutate({ ...row, published: checked })}
          aria-label={`Öffentliche Seite ${BUNDESLAND_LABELS[bundesland]} freigeschaltet`}
        />
        {row.published ? "freigeschaltet" : "nicht freigeschaltet"}
      </label>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        Bearbeiten
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-brand-petrol">
              Öffentliche Seite {BUNDESLAND_LABELS[bundesland]}
            </DialogTitle>
            <DialogDescription>{landesseitePath(bundesland)}</DialogDescription>
          </DialogHeader>
          <Button asChild type="button" variant="outline" size="sm">
            <Link to="/admin/wahlkalender/vorschau/$bundesland" params={{ bundesland: bundeslandSlug(bundesland) }} target="_blank" rel="noopener noreferrer">Entwurf ansehen</Link>
          </Button>
          <LandesseiteForm
            row={row}
            saving={mutation.isPending}
            onSave={(next) => {
              mutation.mutate(next);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LandesseiteForm({
  row,
  onSave,
  saving,
}: {
  row: LandesseiteAdminRow;
  onSave: (row: LandesseiteAdminRow) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<LandesseiteAdminRow>(row);

  const set = <K extends keyof LandesseiteAdminRow>(key: K, value: LandesseiteAdminRow[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <div className="grid gap-6">
      <fieldset className="grid gap-4">
        <legend className="font-display text-base text-brand-petrol">Inhalt der Seite</legend>
      <AdminField label="Überschrift">
        {(props) => (
          <input
            {...props}
            className={adminInputClass}
            value={draft.heading}
            onChange={(event) => set("heading", event.target.value)}
          />
        )}
      </AdminField>
      <AdminField label="Einleitung (ein Absatz je Leerzeile)">
        {(props) => (
          <textarea
            {...props}
            className={adminInputClass}
            rows={5}
            value={draft.introParagraphs.join("\n\n")}
            onChange={(event) =>
              set(
                "introParagraphs",
                event.target.value
                  .split(/\n\s*\n/u)
                  .map((entry) => entry.trim())
                  .filter(Boolean),
              )
            }
          />
        )}
      </AdminField>
      {draft.sections.map((section, index) => (
        <div key={index} className="rounded border border-dashed border-foreground/20 p-3">
          <AdminField label={`Abschnitt ${index + 1}: Überschrift`}>
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                value={section.heading}
                onChange={(event) =>
                  set(
                    "sections",
                    draft.sections.map((entry, i) =>
                      i === index ? { ...entry, heading: event.target.value } : entry,
                    ),
                  )
                }
              />
            )}
          </AdminField>
          <AdminField label="Absätze (ein Absatz je Leerzeile)">
            {(props) => (
              <textarea
                {...props}
                className={adminInputClass}
                rows={5}
                value={section.paragraphs.join("\n\n")}
                onChange={(event) =>
                  set(
                    "sections",
                    draft.sections.map((entry, i) =>
                      i === index
                        ? {
                            ...entry,
                            paragraphs: event.target.value
                              .split(/\n\s*\n/u)
                              .map((p) => p.trim())
                              .filter(Boolean),
                          }
                        : entry,
                    ),
                  )
                }
              />
            )}
          </AdminField>
        </div>
      ))}
      <AdminField label="Überschrift des Verzeichnisses">
        {(props) => (
          <input
            {...props}
            className={adminInputClass}
            value={draft.directoryHeading}
            onChange={(event) => set("directoryHeading", event.target.value)}
          />
        )}
      </AdminField>
      <AdminField label="Einleitung des Verzeichnisses (X = Anzahl)">
        {(props) => (
          <textarea {...props} className={adminInputClass} rows={2} value={draft.directoryIntro}
            onChange={(event) => set("directoryIntro", event.target.value)} />
        )}
      </AdminField>
      </fieldset>

      {/* Freigabe 28.09.2026: Suchmaschinen- und Teilen-Angaben getrennt. */}
      <fieldset className="grid gap-4 border-t border-border pt-6">
        <legend className="font-display text-base text-brand-petrol">Suchmaschinen und Teilen</legend>
        <AdminField label="Suchmaschinen-Titel">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              value={draft.seoTitle}
              onChange={(event) => set("seoTitle", event.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Suchmaschinen-Beschreibung">
          {(props) => (
            <textarea
              {...props}
              className={adminInputClass}
              rows={3}
              value={draft.seoDescription}
              onChange={(event) => set("seoDescription", event.target.value)}
            />
          )}
        </AdminField>
        <ShareImageField
          bundesland={draft.bundesland}
          value={draft.shareImageUrl}
          onChange={(next) => set("shareImageUrl", next)}
        />
      </fieldset>

      <div className="border-t border-border pt-6">
        <Button type="button" disabled={saving} onClick={() => onSave(draft)}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

/**
 * Vorschaubild beim Teilen: Upload in den vorhandenen Leadmagnet-Bildspeicher.
 * Die gespeicherte creategy-Adresse bleibt dauerhaft, der Ablagebereich geschützt.
 */
function ShareImageField({
  bundesland,
  value,
  onChange,
}: {
  bundesland: Bundesland;
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);

  async function removeOwnedImage(url: string | null) {
    const path = ownWahlkalenderImagePath(url);
    if (!path) return;
    const { error } = await supabase.storage.from(LEAD_MAGNET_PREVIEW_BUCKET).remove([path]);
    if (error) throw new Error(error.message);
  }

  async function upload(file: File) {
    if (!ALLOWED_SHARE_IMAGE_TYPES.has(file.type)) {
      toast.error("Bitte laden Sie ein JPG-, PNG- oder WebP-Bild hoch.");
      return;
    }
    setUploading(true);
    try {
      const path = wahlkalenderImagePath(bundesland, file.name, crypto.randomUUID());
      const { error } = await supabase.storage
        .from(LEAD_MAGNET_PREVIEW_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = value;
      onChange(permanentImageUrl("wahlkalender", path));
      try {
        await removeOwnedImage(previous);
      } catch (removeError) {
        console.error("[wahlkalender] Altes Vorschaubild konnte nicht entfernt werden", removeError);
      }
      toast.success("Vorschaubild hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    try {
      await removeOwnedImage(value);
      onChange(null);
      toast.success("Vorschaubild entfernt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Entfernen fehlgeschlagen.");
    }
  }

  return (
    <div className="grid gap-2">
      <span className="font-body text-sm font-medium">Vorschaubild beim Teilen</span>
      {value ? (
        <img src={value} alt="Aktuelles Vorschaubild" className="h-28 w-48 rounded-sm object-cover" />
      ) : (
        <span className="font-body text-xs text-muted-foreground">Kein Vorschaubild hinterlegt.</span>
      )}
      <AdminUploadButton
        label={value ? "Vorschaubild ersetzen" : "Vorschaubild hochladen"}
        accept="image/jpeg,image/png,image/webp"
        uploading={uploading}
        currentName={ownWahlkalenderImagePath(value)?.split("/").pop() ?? null}
        hint="JPG, PNG oder WebP. Nach dem Hochladen bitte die Landesseite speichern."
        onFiles={(files) => {
          const file = files[0];
          if (file) void upload(file);
        }}
      >
        {value ? (
          <Button type="button" variant="outline" size="sm" onClick={() => void remove()}>
            Entfernen
          </Button>
        ) : null}
      </AdminUploadButton>
    </div>
  );
}
